import { Sort } from "@mui/icons-material";
import {
  Box,
  Dialog,
  Button,
  LinearProgress,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  ClickAwayListener,
  RadioGroup,
  Radio,
  Checkbox,
} from "@mui/material";
import axios, { CancelTokenSource } from "axios";
import cx from "classnames";
import { useFormik, FormikProvider, Field } from "formik";
import { debounce, Dictionary, groupBy } from "lodash";
import PQueue from "p-queue";
import React, {
  SetStateAction,
  useState,
  useRef,
  useEffect,
  useContext,
} from "react";
import Dropzone from "react-dropzone";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { ReactComponent as DeleteIcon } from "assets/delete-icon.svg";
import { ReactComponent as List } from "assets/list.svg";
import { ReactComponent as RenameIcon } from "assets/pencil-icon.svg";
import { ReactComponent as Preview } from "assets/preview.svg";
import { ReactComponent as SearchIcon } from "assets/search.svg";
import { ReactComponent as UploadIcon } from "assets/upload-icon.svg";
import { ReactComponent as UploadShotIcon } from "assets/upload-shot.svg";
import { ReactComponent as XMark } from "assets/x-mark.svg";
import Guide from "components/Guide";
import SlapshotInfiniteScroll from "components/SlapshotInfiniteScroll";
import {
  SLAPSHOT_MY_PLAN_URL,
  SLAPSHOT_MY_PLAN_UPGRADE_URL,
  SLAPSHOT_SUBSCRIPTION_UPGRADE_URL,
} from "constants/urls";
import { UserContext } from "layouts/SlapshotMainLayout";
import { ShotCompact } from "models/Shot";
import { Subscription } from "models/User";

import styles from "./Shots.module.scss";
import {
  UserGuides,
  SERVICES,
  STATUSES,
  ViewModes,
  SHOT_SOURCES,
} from "../../constants/constants";
import { FILE_CHUNK_SIZE, MAX_REQUESTS_COUNT } from "../../constants/env";
import { Project } from "../../models/Project";
import {
  getSignedUrlByProject,
  completeMultipartUpload,
  abortMultipartUpload,
} from "../../services/S3Service";
import {
  createShot,
  deleteShot,
  getShots,
  renameShot,
} from "../../services/ShotService";
import { formatDate, formatSize } from "../../utils/helper";
import { InputField } from "../InputField";

const MAX_ALLOWED_FILES = 10;

export interface UploadInfo {
  [name: string]: {
    is_uploaded: boolean;
    total: number;
    loaded: number;
    upload_id?: string;
    sources: CancelTokenSource[];
  };
}

enum SortOptions {
  CREATED_DESC = "By upload date",
  NAME_ASC = "A to Z",
  NAME_DESC = "Z to A",
}

interface ShotsWrapperProps {
  shots: Dictionary<ShotCompact[]>;
  setShots: React.Dispatch<SetStateAction<Dictionary<ShotCompact[]>>>;
  project: Project;
  service: SERVICES;
  isShotsLoading: boolean;
  setIsShotsLoading: React.Dispatch<SetStateAction<boolean>>;
  subscription: Subscription | null;
}

interface ShotsListProps {
  shots: Dictionary<ShotCompact[]>;
  project: Project;
  service: SERVICES;
  shotsViewMode: ViewModes;
  uploadInfo: UploadInfo;
  isDragActive: boolean;
  setShots: React.Dispatch<SetStateAction<Dictionary<ShotCompact[]>>>;
  isClickable: (shot: ShotCompact) => boolean | "";
  isUploading: (id: number, name: string) => boolean;
  cancelOngoingUpload: (name: string) => void;
  handleContextMenu: (e, s: ShotCompact) => void;
  open: () => void;
  setIsShotsLoading: React.Dispatch<SetStateAction<boolean>>;
  setShotsCount: React.Dispatch<SetStateAction<number>>;
  shotsCount: number;
  sortBy: string;
  page: number;
  setPage: React.Dispatch<SetStateAction<number>>;
  subscription: Subscription | null;
}

const ShotsList: React.FC<ShotsListProps> = ({
  shots,
  project,
  service,
  shotsViewMode,
  uploadInfo,
  setShots,
  isClickable,
  isUploading,
  cancelOngoingUpload,
  handleContextMenu,
  isDragActive,
  open,
  setIsShotsLoading,
  setShotsCount,
  shotsCount,
  sortBy,
  page,
  setPage,
  subscription,
}) => {
  const { projectId, shotId } = useParams();
  const navigate = useNavigate();
  const [hoveredShotId, setHoveredShotId] = useState<number>();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [hasNextPage, setHasNextPage] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadMoreShots = () => {
    if (hasNextPage && !loadingMore) {
      setLoadingMore(true);
      setPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (projectId)
      getShots(+projectId, page, searchTerm, sortBy)
        .then((res) => {
          setHasNextPage(!!res.next);
          setShotsCount(res.shots_count);
          if (page === 1) {
            const shotsDict = groupBy(res.results, (shot) =>
              formatDate(shot.created),
            );
            setShots(shotsDict);
          } else {
            const newShotsDict = groupBy(res.results, (shot) =>
              formatDate(shot.created),
            );
            setShots((prevShots) => {
              const mergedShots = { ...prevShots };
              Object.keys(newShotsDict).forEach((date) => {
                if (mergedShots[date]) {
                  mergedShots[date] = [
                    ...mergedShots[date],
                    ...newShotsDict[date],
                  ];
                } else {
                  mergedShots[date] = newShotsDict[date];
                }
              });
              return mergedShots;
            });
          }
        })
        .finally(() => {
          setIsLoading(false);
          setLoadingMore(false);
          if (page === 1) setIsShotsLoading(false);
        });
  }, [
    project,
    projectId,
    page,
    setShots,
    setIsShotsLoading,
    searchTerm,
    setShotsCount,
    sortBy,
  ]);

  const getStatusClassName = (status: string) => {
    switch (status) {
      case "IN_PROGRESS":
        return styles.inferencing;
      case "COMPLETED":
        return styles.statusCompleted;
      case "FAILED":
        return styles.statusFailed;
      // no default
    }
    return "";
  };

  const onSearchInput = useRef(
    debounce((term) => {
      setPage(1);
      setSearchTerm(term);
    }, 500),
  ).current;

  return !isDragActive && shotsCount > 0 ? (
    <>
      <div className={styles.searchContainer}>
        <SearchIcon />

        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search..."
          onChange={(e) => {
            onSearchInput.cancel();
            onSearchInput(e.target.value);
          }}
        />
      </div>

      <div className={styles.shots}>
        <SlapshotInfiniteScroll
          hasNextPage={hasNextPage}
          loadMore={loadMoreShots}
        >
          <>
            {Object.keys(shots).map((createdDate) => (
              <div key={createdDate} className={styles.shotsByDate}>
                {!project.is_demo_project && (
                  <div className={styles.dateContainer}>
                    <div className={styles.date}>{createdDate}</div>
                  </div>
                )}
                {shots[createdDate].map((s, index) => (
                  <div
                    key={index}
                    id={`shots_view_shot_${s.id}`}
                    className={cx(
                      styles.shotContainer,
                      getStatusClassName(s.latest_inference_status[service]),
                      shotId && +shotId === s.id && styles.selectedShot,
                      isClickable(s) && styles.clickableShot,
                      shotId &&
                        +shotId === s.id &&
                        s.latest_inference_status[service] ===
                          STATUSES.IN_PROGRESS &&
                        styles.selectedShotInferencing,
                    )}
                    onMouseEnter={() => {
                      setHoveredShotId(s.id);
                    }}
                    onMouseLeave={() => {
                      setHoveredShotId(undefined);
                    }}
                    onClick={() => {
                      if (isClickable(s)) {
                        navigate(
                          `/projects/${project.id}/${service}/shots/${s.id}`,
                        );
                      }
                    }}
                    onContextMenu={(e) => {
                      handleContextMenu(e, s);
                    }}
                    style={{
                      marginBottom:
                        project.is_demo_project && !s.is_demo_shot
                          ? "10px"
                          : "0px",
                    }}
                  >
                    {s.deleted && (
                      <div className={styles.deletedLabel}>Deleted</div>
                    )}
                    {shotsViewMode === ViewModes.Preview && (
                      <>
                        <div className={styles.shotThumbnail}>
                          {s.width && s.height && (
                            <div className={styles.resolutionBadge}>
                              {s.width} x {s.height}
                            </div>
                          )}
                          {isUploading(s.id, s.name) &&
                            (hoveredShotId !== s.id ? (
                              <div className={styles.shotProcessingContainer}>
                                <p>Uploading...</p>
                                <Box
                                  sx={{
                                    position: "relative",
                                    top: "20px",
                                    width: "calc(100% - 10px)",
                                    mt: 2,
                                    borderRadius: "5px",
                                    border: "1px solid #AEAEAE",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: "-25px",
                                      left: `${Math.round(
                                        (uploadInfo[s.name].loaded /
                                          uploadInfo[s.name].total) *
                                          100,
                                      )}%`,
                                      transform: "translateX(-50%)",
                                    }}
                                  >
                                    <Tooltip
                                      title={`${Math.round(
                                        (uploadInfo[s.name].loaded /
                                          uploadInfo[s.name].total) *
                                          100,
                                      )}%`}
                                      arrow
                                      placement="top"
                                    >
                                      <Typography
                                        variant="body2"
                                        color="text.primary"
                                        sx={{
                                          background: "black",
                                          padding: "2px 5px",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        {`${Math.round(
                                          (uploadInfo[s.name].loaded /
                                            uploadInfo[s.name].total) *
                                            100,
                                        )}%`}
                                      </Typography>
                                    </Tooltip>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.round(
                                      (uploadInfo[s.name].loaded /
                                        uploadInfo[s.name].total) *
                                        100,
                                    )}
                                    sx={{
                                      height: "4px",
                                      borderRadius: "5px",
                                      "& .MuiLinearProgress-bar": {
                                        backgroundColor: "#AEAEAE",
                                        border: "1px solid #AEAEAE",
                                      },
                                      backgroundColor: "transparent",
                                    }}
                                  />
                                </Box>
                              </div>
                            ) : (
                              <div className={styles.shotProcessingContainer}>
                                <button
                                  type="button"
                                  className={styles.cancelUploadButton}
                                  onClick={() => {
                                    cancelOngoingUpload(s.name);
                                  }}
                                >
                                  Cancel Upload
                                </button>
                              </div>
                            ))}
                          {!isUploading(s.id, s.name) &&
                            !s.shot_preview_image_url && (
                              <p>Fetching thumbnail...</p>
                            )}
                          {!isUploading(s.id, s.name) &&
                            s.preprocessing_stage ===
                              "GENERATING_H264_PREVIEW" &&
                            s.shot_preview_image_url && (
                              <div className={styles.shotProcessingContainer}>
                                <p>Processing...</p>
                                <div className={styles.loader}>
                                  <div className={styles.pipe} />
                                  <div className={styles.dashes} />
                                </div>
                              </div>
                            )}
                          {!isUploading(s.id, s.name) &&
                            s.preprocessing_stage === "EXTRACTING_FRAMES" &&
                            s.shot_preview_image_url && (
                              <div className={styles.shotProcessingContainer}>
                                <p>Finalizing...</p>
                                <div className={styles.loader}>
                                  <div className={styles.pipe} />
                                  <div className={styles.dashes} />
                                </div>
                              </div>
                            )}
                          {!isUploading(s.id, s.name) &&
                            s.preprocessing_stage === "FAILED" && (
                              <div className={styles.shotProcessingContainer}>
                                <p>{s.error_message || "Processing Failed"}</p>
                              </div>
                            )}
                          {s.shot_preview_image_url && (
                            <img
                              alt={s.shot_preview_image_url}
                              className={styles.thumbnailImage}
                              src={s.shot_preview_image_url}
                            />
                          )}
                        </div>
                        <div className={styles.shotDetails}>
                          <div className={styles.name}>{s.name}</div>
                        </div>
                      </>
                    )}
                    {shotsViewMode === ViewModes.List && (
                      <div className={styles.listShotContainer}>
                        <div className={styles.listShotThumbnail}>
                          {s.shot_preview_image_url && (
                            <img src={s.shot_preview_image_url} alt="" />
                          )}
                        </div>
                        <div className={styles.listShotDetails}>
                          <h5 className={styles.name}>{s.name}</h5>
                          {isUploading(s.id, s.name) &&
                            (hoveredShotId !== s.id ? (
                              <div
                                className={styles.listShotProcessingContainer}
                              >
                                <p>Uploading...</p>
                                <p>
                                  {Math.round(
                                    (uploadInfo[s.name].loaded /
                                      uploadInfo[s.name].total) *
                                      100,
                                  )}
                                  %
                                </p>
                              </div>
                            ) : (
                              <div className={styles.cancelUploadListContainer}>
                                <button
                                  type="button"
                                  className={styles.cancelUploadListButton}
                                  onClick={() => {
                                    cancelOngoingUpload(s.name);
                                  }}
                                >
                                  Cancel Upload
                                </button>
                              </div>
                            ))}
                          {!isUploading(s.id, s.name) &&
                            !s.shot_preview_image_url && (
                              <div
                                className={styles.listShotProcessingContainer}
                              >
                                <p>Fetching thumbnail...</p>
                              </div>
                            )}
                          {!isUploading(s.id, s.name) &&
                            s.preprocessing_stage ===
                              "GENERATING_H264_PREVIEW" &&
                            s.shot_preview_image_url && (
                              <div
                                className={styles.listShotProcessingContainer}
                              >
                                <p>Processing...</p>
                                <div className={styles.listLoader}>
                                  <div className={styles.listPipe} />
                                  <div className={styles.listDashes} />
                                </div>
                              </div>
                            )}
                          {!isUploading(s.id, s.name) &&
                            s.preprocessing_stage === "EXTRACTING_FRAMES" &&
                            s.shot_preview_image_url && (
                              <div
                                className={styles.listShotProcessingContainer}
                              >
                                <p>Finalizing...</p>
                                <div className={styles.listLoader}>
                                  <div className={styles.listPipe} />
                                  <div className={styles.listDashes} />
                                </div>
                              </div>
                            )}
                          {!isUploading(s.id, s.name) &&
                            s.preprocessing_stage === "FAILED" && (
                              <div
                                className={styles.listShotProcessingContainer}
                              >
                                <p>{s.error_message || "Processing Failed"}</p>
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
            {hasNextPage && (
              <div className={styles.loadingIndicator}>
                {loadingMore
                  ? "Loading more shots..."
                  : "Scroll to load more shots"}
              </div>
            )}
          </>
        </SlapshotInfiniteScroll>
      </div>
      <div className={styles.shotsFooter}>
        <button
          type="button"
          className={cx(styles.footerButton, styles.button)}
          style={{ width: "100%" }}
          onClick={open}
        >
          <UploadIcon style={{ marginRight: "4px" }} />
          Upload shots
        </button>

        {subscription && (
          <div className={styles.storageStat}>
            <span>{formatSize(subscription.storage_used)}</span>
            <span
              className={styles.statSecondary}
            >{` / ${formatSize(subscription.plan.storage, "GB")}`}</span>
          </div>
        )}
      </div>
    </>
  ) : (
    <div className={styles.mainContainer}>
      <div className={styles.iconContainer}>
        <UploadShotIcon />
      </div>
      <h3>Drag and drop video files here</h3>
      <button
        type="button"
        className={cx(styles.footerButton, styles.button)}
        onClick={open}
      >
        + Upload shots
      </button>
      <div className={styles.description}>
        <p>mp4 or mov files supported</p>
      </div>
    </div>
  );
};
const ShotsWrapper: React.FC<ShotsWrapperProps> = ({
  shots,
  setShots,
  subscription,
  project,
  service,
  isShotsLoading,
  setIsShotsLoading,
}) => {
  const { shotId, projectId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useContext(UserContext);

  const [files, setFiles] = useState<File[]>([]);
  const [uploadInfo, setUploadInfo] = useState<UploadInfo>({});
  const [uploadStartTime, setUploadStartTime] = useState<Date>();
  const [signedUrlCancelTokenSource, setSignedUrlCancelTokenSource] = useState<
    Record<string, CancelTokenSource>
  >({});

  const [menuShot, setMenuShot] = useState<null | {
    id: number;
    name: string;
    created: Date;
    is_demo_shot: boolean;
  }>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showRenameShotModal, setShowRenameShotModal] = useState(false);
  const [uploadExceedSize, setUploadExceedSize] = useState<number | null>(null);
  const [failedShots, setFailedShots] = useState<string[]>([]);
  const [shotsViewMode, setShotsViewMode] = useState<ViewModes>(
    ViewModes.Preview,
  );
  const [showSortByModal, setShowSortByModal] = useState(false);
  const [sortBy, setSortBy] =
    useState<keyof typeof SortOptions>("CREATED_DESC");
  const [shotsCount, setShotsCount] = useState(0);
  const [page, setPage] = useState(1);

  const toggleViewMode = (
    mode: ViewModes,
    setViewMode: React.Dispatch<SetStateAction<ViewModes>>,
  ) => {
    setViewMode(ViewModes[mode]);
  };

  const renameShotFormik = useFormik({
    initialValues: {
      name: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
    }),
    onSubmit: async (values) => {
      if (menuShot)
        renameShot(menuShot.id, values.name)
          .then((res) => {
            setShots((prevShots) => {
              const date = formatDate(menuShot.created);
              const updatedShots = { ...prevShots };
              updatedShots[date] = updatedShots[date].map((s) => {
                if (s.id === res.id) {
                  return res;
                }
                return s;
              });
              return updatedShots;
            });
          })
          .finally(() => {
            setShowRenameShotModal(false);
          });
    },
  });

  const filesQueue = useRef(
    new PQueue({ concurrency: MAX_REQUESTS_COUNT }),
  ).current;

  const startUpload = async (filesToUpload: File[]) => {
    return new Promise(async (onComplete, onFailed) => {
      setFiles((prevFiles) => [...prevFiles, ...filesToUpload]);
      const newFiles = filesToUpload.filter(
        (file) => !files.some((f) => f.name === file.name),
      );
      const newUploadInfo: UploadInfo = {};
      newFiles.forEach((file) => {
        const shotName = file.name.substring(0, file.name.lastIndexOf("."));
        newUploadInfo[shotName] = {
          is_uploaded: false,
          total: file.size,
          loaded: 0,
          sources: [],
        };
      });
      setUploadInfo((prevInfo) => ({ ...prevInfo, ...newUploadInfo }));
      setUploadStartTime(new Date());

      const partsConcurrency =
        newFiles.length > MAX_REQUESTS_COUNT
          ? 1
          : MAX_REQUESTS_COUNT - newFiles.length + 1;

      newFiles.forEach((file) => {
        const shotName = file.name.substring(0, file.name.lastIndexOf("."));
        filesQueue.add(async () => {
          const cancelTokenSource = axios.CancelToken.source();
          setSignedUrlCancelTokenSource((prev) => ({
            ...prev,
            [shotName]: cancelTokenSource,
          }));
          let signedUrls;
          try {
            signedUrls =
              projectId && (await getSignedUrlByProject(projectId, file));
          } catch (error) {
            if (
              typeof error === "object" &&
              error !== null &&
              "detail" in error
            ) {
              toast.error((error as Record<string, unknown>).detail as string);
            }
          }
          if (!signedUrls) {
            return;
          }

          /* eslint-disable camelcase */
          const { urls, key, upload_id } = signedUrls;
          const partsQueue = new PQueue({ concurrency: partsConcurrency });
          // To store progress of file parts
          const uploaded: { [index: number]: number } = {};
          const parts: { eTag: string; partNumber: number }[] = [];

          let uploadAborted = false;
          await Promise.all(
            urls.map((url, index) =>
              partsQueue.add(async () => {
                const start = index * FILE_CHUNK_SIZE;
                const end = (index + 1) * FILE_CHUNK_SIZE;
                const blob =
                  index < urls.length
                    ? file.slice(start, end)
                    : file.slice(start);
                const tokenSource = axios.CancelToken.source();

                setUploadInfo((prevInfo) => ({
                  ...prevInfo,
                  [shotName]: {
                    ...prevInfo[shotName],
                    sources: [
                      ...(prevInfo[shotName]?.sources || []),
                      tokenSource,
                    ],
                  },
                }));

                const res = await axios
                  .put(url, blob, {
                    headers: { "Content-Type": file.type },
                    withCredentials: true,
                    cancelToken: tokenSource.token,
                    onUploadProgress: ({ total, loaded }) => {
                      uploaded[index] = loaded;
                      // Updating upload progress.
                      setUploadInfo((prevInfo) => ({
                        ...prevInfo,
                        [shotName]: {
                          ...prevInfo[shotName],
                          loaded: Object.values(uploaded).reduce(
                            (t, v) => t + v,
                            0,
                          ),
                        },
                      }));
                    },
                  })
                  .catch(async (err) => {
                    if (!uploadAborted) {
                      uploadAborted = true;
                      partsQueue.clear();
                      await abortMultipartUpload(upload_id, key);
                    }
                  });
                if (res) {
                  parts.push({ eTag: res.headers.etag, partNumber: index + 1 });
                  if (parts.length === urls.length) {
                    completeMultipartUpload(upload_id, key, parts).then((r) => {
                      setUploadInfo((prevInfo) => {
                        const updatedInfo = {
                          ...prevInfo,
                          [shotName]: {
                            ...prevInfo[shotName],
                            is_uploaded: true,
                          },
                        };
                        if (projectId)
                          createShot(+projectId, file.name, r.Key).then(
                            (resp) => {
                              const date = formatDate(resp.created);
                              setShots((prevShots) => {
                                const updatedShots = { ...prevShots };
                                updatedShots[date] = updatedShots[date].map(
                                  (s) => {
                                    if (s.id === -1 && s.name === resp.name) {
                                      return resp;
                                    }
                                    return s;
                                  },
                                );
                                return updatedShots;
                              });
                              setFiles((fs) =>
                                fs.filter((f) => f.name !== file.name),
                              );
                            },
                          );
                        if (
                          Object.values(updatedInfo).length > 0 &&
                          Object.values(updatedInfo).every(
                            (v) => v.is_uploaded === true,
                          )
                        ) {
                          onComplete(updatedInfo);
                        }
                        return updatedInfo;
                      });
                    });
                  }
                }
              }),
            ),
          );
        });
      });
    });
  };

  const cancelOngoingUpload = async (name: string) => {
    const fileInfo = uploadInfo[name];
    if (fileInfo) {
      if (!fileInfo.is_uploaded && fileInfo.sources.length) {
        fileInfo.sources.forEach((source) => source.cancel("Cancelled"));
      }
      const signedUrlSource = signedUrlCancelTokenSource[name];
      if (signedUrlSource) {
        signedUrlSource.cancel("Cancelled");
      }
    }
    setUploadInfo((prevInfo) => {
      const { [name]: removed, ...rest } = prevInfo;
      return rest;
    });
    setSignedUrlCancelTokenSource((prevSource) => {
      const { [name]: removed, ...rest } = prevSource;
      return rest;
    });
    setFiles((fs) => {
      return fs.filter(
        (f) => f.name.substring(0, f.name.lastIndexOf(".")) !== name,
      );
    });
    setShots((prevShots) => {
      const date = formatDate(new Date());
      const updatedShots = { ...prevShots };
      if (updatedShots[date]) {
        updatedShots[date] = updatedShots[date].filter((s) => s.name !== name);
        if (updatedShots[date].length === 0) {
          delete updatedShots[date];
        }
      }
      return updatedShots;
    });
    setShotsCount((prev) => prev - 1);
  };

  const isUploading = (id: number, shotName: string) => {
    return (
      id === -1 &&
      files.some((file) => {
        const nameWithoutExtension = file.name.substring(
          0,
          file.name.lastIndexOf("."),
        );
        return nameWithoutExtension === shotName;
      })
    );
  };

  const isClickable = (s: ShotCompact) => {
    return (
      !isUploading(s.id, s.name) &&
      s.shot_preview_image_url &&
      s.preprocessing_stage === "COMPLETED"
    );
  };

  const showSelectShotGuide = () => {
    return !!Object.values(shots)
      .flat()
      .find((shot) => isClickable(shot));
  };

  const handleContextMenu = (e, s: ShotCompact) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setMenuShot({
      id: s.id,
      name: s.name,
      created: s.created,
      is_demo_shot: s.is_demo_shot,
    });
    renameShotFormik.setFieldValue("name", s.name);
  };

  useEffect(() => {
    if (!shotId || !searchParams.get("source")) return;
    const shot = document.getElementById(`shots_view_shot_${shotId}`);
    if (shot) shot.scrollIntoView();
  }, [searchParams, shotId, shots]);

  return (
    <>
      <div className={styles.shotsHeader}>
        <div className={styles.shotsHeaderTitle}>
          <span>Shots</span>
          <span className={styles.shotsCount}>({shotsCount})</span>
        </div>

        <div className={styles.actionsContainer}>
          <ToggleButtonGroup
            value={shotsViewMode}
            exclusive
            onChange={(e, mode) =>
              mode && toggleViewMode(mode, setShotsViewMode)
            }
            aria-label="View Mode"
            size="small"
          >
            <ToggleButton
              value="List"
              title="list"
              style={{ border: 0, padding: 0 }}
            >
              <List
                className={cx(
                  styles.viewIcon,
                  shotsViewMode === "List" && styles.viewIconActive,
                )}
              />
            </ToggleButton>
            <ToggleButton
              style={{ border: 0, padding: 0 }}
              value="Preview"
              title="Preview"
            >
              <Preview
                className={cx(
                  styles.viewIcon,
                  shotsViewMode === "Preview" && styles.viewIconActive,
                )}
              />
            </ToggleButton>
          </ToggleButtonGroup>

          <ClickAwayListener onClickAway={() => setShowSortByModal(false)}>
            <Tooltip
              open={showSortByModal}
              arrow
              placement="bottom"
              slotProps={{
                tooltip: {
                  sx: {
                    padding: "0 !important",
                    bgcolor: "#22242A",
                    borderRadius: "8px",
                    "& .MuiTooltip-arrow": {
                      color: "#22242A",
                    },
                    border: "1px solid #CCCCDC1F",
                    boxShadow: "0px 8px 24px 0px #010409",
                  },
                },
              }}
              title={
                <div
                  className={styles.sortTooltip}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className={styles.sortTooltipTitle}>Sort by:</div>

                  <RadioGroup
                    aria-label="service"
                    name="service"
                    value={sortBy}
                    onChange={(e) => {
                      setPage(1);
                      setSortBy(e.target.value as keyof typeof SortOptions);
                      setTimeout(() => {
                        setShowSortByModal(false);
                      }, 300);
                    }}
                  >
                    {Object.entries(SortOptions).map(([key, value]) => (
                      <FormControlLabel
                        key={key}
                        value={key}
                        control={
                          <Radio
                            sx={{
                              color: "#CCCCDCA6",
                              "&.Mui-checked": {
                                color: "#6565EC",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: "14px",
                              },
                            }}
                          />
                        }
                        label={value}
                        sx={{
                          "&.MuiFormControlLabel-root": {
                            margin: "0px !important",
                            marginLeft: "-2px !important",
                            width: "fit-content !important",
                          },
                          "& .MuiFormControlLabel-label": {
                            color: "#CCCCDC",
                            fontSize: "14px",
                            fontFamily: "Inter",
                            marginLeft: "6px",
                          },
                          "& .Mui-checked + .MuiFormControlLabel-label": {
                            color: "#FFFFFFE5",
                            fontWeight: 500,
                          },
                        }}
                      />
                    ))}
                  </RadioGroup>
                </div>
              }
            >
              <div
                className={styles.sortIcon}
                onClick={() => setShowSortByModal(!showSortByModal)}
              >
                <Sort />
              </div>
            </Tooltip>
          </ClickAwayListener>
        </div>
      </div>

      <Guide
        id={!shotsCount ? UserGuides.UploadShot : UserGuides.SelectShot}
        open={(showSelectShotGuide() || !shotsCount) && !isShotsLoading}
        title={!shotsCount ? "Add files from here" : "Select shot from here"}
        description={
          !shotsCount
            ? "Either drag and drop files or click on upload shots button."
            : "Click on a shot to view in the player."
        }
        placement="right"
      >
        <div>
          <Dropzone
            accept={{
              "video/mp4": [".mp4"],
              "video/quicktime": [".mov"],
            }}
            maxFiles={MAX_ALLOWED_FILES}
            noClick
            onDrop={async (acceptedFiles) => {
              if (!subscription) return;
              const uploadSize = acceptedFiles.reduce(
                (total, file) => total + file.size,
                0,
              );
              if (
                !subscription.is_free_trial_period &&
                uploadSize + subscription.storage_used >
                  subscription.plan.storage * 1024 ** 3
              ) {
                setUploadExceedSize(uploadSize);
                return;
              }

              const validFiles: File[] = [];
              acceptedFiles.forEach((file) => {
                const shotName = file.name.substring(
                  0,
                  file.name.lastIndexOf("."),
                );
                const SHOT_NAME_PATTERN =
                  /^(?=.{3,}$)(?![\s])[a-zA-Z0-9\s\-_]+$/;
                if (!SHOT_NAME_PATTERN.test(shotName)) {
                  setFailedShots((prevShots) => [...prevShots, shotName]);
                  return;
                }
                validFiles.push(file);

                const date = formatDate(new Date());
                const shot: ShotCompact = {
                  id: -1,
                  name: shotName,
                  first_frame_number: 0,
                  frame_count: -1,
                  latest_inference_status: {},
                  created: new Date(),
                  shot_preview_image_url: "",
                  show_export_button: false,
                  preprocessing_stage: "PROCESSING",
                  error_message: "",
                  fps: 0,
                  start_time: 0,
                  is_demo_shot: false,
                  source: SHOT_SOURCES.HS,
                  width: 0,
                  height: 0,
                };

                setShots((prevShots) => {
                  let updatedShots = { ...prevShots };
                  if (updatedShots[date]) {
                    updatedShots[date].unshift(shot);
                  } else {
                    updatedShots = {
                      [date]: [shot],
                      ...updatedShots,
                    };
                  }
                  return updatedShots;
                });
                setShotsCount((prev) => prev + 1);
              });
              if (projectId) startUpload(validFiles);
            }}
          >
            {({ getRootProps, getInputProps, open, isDragActive }) => {
              return (
                <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  {isDragActive && (
                    <div className={styles.mainContainer}>
                      <div className={styles.largeIconContainer}>
                        <UploadShotIcon />
                      </div>
                    </div>
                  )}

                  <Dialog
                    open={!!uploadExceedSize}
                    slotProps={{
                      paper: {
                        className: "dialog",
                      },
                    }}
                  >
                    <div className="modalContainer">
                      <div className="modalHeader">
                        <h3 className="modalTitle">Asset Storage Quota</h3>
                        <button
                          onClick={() => {
                            setUploadExceedSize(null);
                          }}
                          type="button"
                          className="closeButton"
                        >
                          <XMark />
                        </button>
                      </div>
                      <div className={styles.qmContent}>
                        <strong>Asset Storage used:</strong>
                        <p
                          className={styles.qmQuota}
                        >{`${formatSize(subscription?.storage_used)} of ${formatSize(subscription?.plan.storage, "GB")}`}</p>
                        <div
                          className={styles.qmDetails}
                          style={{
                            visibility: uploadExceedSize ? "visible" : "hidden",
                          }}
                        >
                          <p>
                            {`You are uploading `}
                            <strong>{formatSize(uploadExceedSize)}</strong>
                            {` of assets. This upload will take you above your storage quota of `}
                            <strong>
                              {formatSize(subscription?.plan.storage, "GB")}
                            </strong>
                            . (
                            <strong>
                              {formatSize(subscription?.storage_used)}
                            </strong>
                            {` used till date)`}
                          </p>
                          <p>
                            Please trim the videos to proceed <b>OR</b> upgrade
                            to increase your storage quote
                          </p>
                        </div>
                      </div>
                      <div className={styles.qmActions}>
                        <Button
                          disableRipple
                          className={styles.qmRetry}
                          onClick={() => {
                            setUploadExceedSize(null);
                            open();
                          }}
                        >
                          Retry Upload
                        </Button>
                        {project.company.is_admin && (
                          <Button
                            className={styles.qmUpgrade}
                            onClick={() => {
                              navigate(
                                project.company.is_owner
                                  ? SLAPSHOT_MY_PLAN_UPGRADE_URL
                                  : `${SLAPSHOT_SUBSCRIPTION_UPGRADE_URL}?workspaceId=${project.company.id}&workspaceName=${project.company.name}`,
                              );
                            }}
                          >
                            Upgrade
                          </Button>
                        )}
                      </div>
                    </div>
                  </Dialog>

                  <ShotsList
                    shots={shots}
                    project={project}
                    service={service}
                    shotsViewMode={shotsViewMode}
                    uploadInfo={uploadInfo}
                    setShots={setShots}
                    isUploading={isUploading}
                    isClickable={isClickable}
                    cancelOngoingUpload={cancelOngoingUpload}
                    handleContextMenu={handleContextMenu}
                    isDragActive={isDragActive}
                    open={open}
                    setIsShotsLoading={setIsShotsLoading}
                    setShotsCount={setShotsCount}
                    shotsCount={shotsCount}
                    sortBy={sortBy}
                    page={page}
                    setPage={setPage}
                    subscription={subscription}
                  />
                </div>
              );
            }}
          </Dropzone>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "center", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            open={!!anchorEl}
            onClose={() => {
              setAnchorEl(null);
              setMenuShot(null);
            }}
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "#111113",
                borderRadius: "7px",
                border: "1px solid #CCCCDC1F",
                padding: "8px 6px",
                width: "184px",
                boxShadow: "0px 8px 24px 0px #010409",
              },
            }}
          >
            {menuShot && !isUploading(menuShot.id, menuShot.name) && (
              <>
                {!menuShot.is_demo_shot && (
                  <MenuItem
                    className={styles.menuItem}
                    onClick={() => {
                      setAnchorEl(null);
                      setShowRenameShotModal(true);
                    }}
                  >
                    <RenameIcon style={{ marginRight: "8px" }} />
                    Rename
                  </MenuItem>
                )}
                <MenuItem
                  className={styles.menuItem}
                  onClick={() => {
                    setAnchorEl(null);
                    setShowConfirmationModal(true);
                  }}
                >
                  <DeleteIcon style={{ marginRight: "8px" }} />
                  Delete
                </MenuItem>
              </>
            )}
          </Menu>
          <Dialog
            open={showRenameShotModal}
            slotProps={{
              paper: {
                className: "dialog",
              },
            }}
          >
            <FormikProvider value={renameShotFormik}>
              <form
                className="modalContainer"
                onSubmit={renameShotFormik.handleSubmit}
              >
                <div className="modalHeader">
                  <h3 className="modalTitle">Rename Shot</h3>
                  <button
                    onClick={() => {
                      setShowRenameShotModal(false);
                    }}
                    type="button"
                    className="closeButton"
                  >
                    <XMark />
                  </button>
                </div>
                <div className="modalContent">
                  <p className={styles.modalDesc}>Edit shot name</p>
                  <Field
                    name="name"
                    component={InputField}
                    placeholder="Mission Impossible"
                    label="Shot name"
                  />
                </div>
                <div className="modalActions">
                  <Button type="submit" className="slapShotButton">
                    Update
                  </Button>
                </div>
              </form>
            </FormikProvider>
          </Dialog>
          <Dialog
            open={showConfirmationModal}
            slotProps={{
              paper: {
                className: "dialog",
              },
            }}
          >
            <div className="modalContainer">
              <div className="modalHeader">
                <h3 className="modalTitle">Delete Shot</h3>
                <button
                  onClick={() => {
                    setShowConfirmationModal(false);
                  }}
                  type="button"
                  className="closeButton"
                >
                  <XMark />
                </button>
              </div>
              <div className={styles.confirmationText}>
                <p>
                  Are you sure you want to delete this shot&nbsp;
                  <b>{menuShot && menuShot.name}</b>
                </p>
              </div>
              <div className="modalActions">
                <Button
                  type="button"
                  className={styles.noBackgroundButton}
                  onClick={() => {
                    setShowConfirmationModal(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className={styles.outlineButtonRed}
                  onClick={() => {
                    if (menuShot)
                      deleteShot(menuShot.id).then(() => {
                        setShots((prevShots) => {
                          const date = formatDate(menuShot.created);
                          const updatedShots = { ...prevShots };
                          if (updatedShots[date]) {
                            updatedShots[date] = updatedShots[date].filter(
                              (shot) => shot.id !== menuShot.id,
                            );
                            if (updatedShots[date].length === 0) {
                              delete updatedShots[date];
                            }
                          }
                          return updatedShots;
                        });
                      });
                    setShowConfirmationModal(false);
                  }}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </Dialog>
          <Dialog
            open={!!failedShots.length}
            slotProps={{
              paper: {
                className: "dialog",
              },
            }}
          >
            <div className="modalContainer">
              <div className="modalHeader">
                <h3 className="modalTitle">Error detected during upload</h3>
                <button
                  onClick={() => {
                    setFailedShots([]);
                  }}
                  type="button"
                  className="closeButton"
                >
                  <XMark />
                </button>
              </div>
              <div className={styles.modalText}>
                <p>
                  The {failedShots.length > 1 ? "assets" : "asset"}
                  {failedShots.length > 1 ? (
                    <ul>
                      {failedShots.map((s, i) => (
                        <li key={i}> {s}</li>
                      ))}
                    </ul>
                  ) : (
                    <span> {failedShots[0]}</span>
                  )}
                  {failedShots.length > 1 ? " have" : " has"} a character which
                  will prevent a successful upload. Please rename the file and
                  try to upload it again.
                </p>
              </div>
              <div className="modalActions">
                <Button
                  type="button"
                  className={styles.closeButton}
                  onClick={() => {
                    setFailedShots([]);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </Dialog>
        </div>
      </Guide>
    </>
  );
};

export default ShotsWrapper;
